import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * HR leave and employee request contracts (ERD §7.3; FRD-HRP-025 to HRP-037).
 *
 * Days are decimal strings in half-day steps (`"2"`, `"2.5"`), never JavaScript numbers
 * (SDD §9.2). Balances are derived by the API and returned at two decimals. Short leave
 * (FRD-HRP-080 to HRP-082, CLR-41) is counted in hours, in half-hour steps.
 */
const MasterStatusSchema = z.enum(['active', 'inactive']);
export const REQUEST_STATUSES = [
    'submitted',
    'approved',
    'rejected',
    'returned',
    'cancelled',
];
export const RequestStatusSchema = z.enum(REQUEST_STATUSES);
/** Days in half-day steps: up to three integer digits and an optional `.0` / `.5`. */
export const DaysSchema = z
    .string()
    .regex(/^\d{1,3}(\.[05])?$/, 'Days in half-day steps, e.g. 2 or 2.5');
/** Hours in half-hour steps: up to three integer digits and an optional `.0` / `.5`. */
export const HoursSchema = z
    .string()
    .regex(/^\d{1,3}(\.[05])?$/, 'Hours in half-hour steps, e.g. 2 or 2.5');
/** What a leave type is counted in: days, or hours (short leave, FRD-HRP-080). */
export const LEAVE_UNITS = ['days', 'hours'];
export const ENTITLEMENT_PERIODS = ['calendar_year', 'service_year'];
export const ACCRUAL_BASES = ['upfront', 'monthly_pro_rata'];
// ------------------------------------------------------------------ Leave Type
export const LeaveTypeSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    /** Whether the type has an entitlement and a balance at all (FRD-HRP-026). */
    balanceTracked: z.boolean(),
    annualEntitlementDays: z.string().nullable(),
    entitlementPeriod: z.enum(ENTITLEMENT_PERIODS).nullable(),
    accrualBasis: z.enum(ACCRUAL_BASES).nullable(),
    probationMonths: z.number().int().nullable(),
    carryForwardMaxDays: z.string().nullable(),
    allowNegativeBalance: z.boolean().nullable(),
    /** `hours` is short leave (FRD-HRP-080): the four values below are then all present. */
    countedIn: z.enum(LEAVE_UNITS).default('days'),
    monthlyLimitHours: z.string().nullable().default(null),
    maxHoursPerRequest: z.string().nullable().default(null),
    hoursPerDay: z.string().nullable().default(null),
    /** The days-counted type a request above `maxHoursPerRequest` is converted to (FRD-HRP-082). */
    conversionLeaveTypeId: IdSchema.nullable().default(null),
    /**
     * Above how many days a request needs a reason and a supporting document (CLR-46,
     * FRD-HRP-094): `null` never, `"0"` always.
     */
    supportingRequiredAboveDays: z.string().nullable().default(null),
    status: MasterStatusSchema,
    version: z.number().int(),
});
const LeaveTypeCodeSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_-]{0,19}$/, 'Upper-case letters, digits, - and _, starting with a letter');
const LeaveTypeNameSchema = z.string().trim().min(1).max(100);
const ProbationMonthsSchema = z.number().int().min(0).max(120);
/**
 * Every rule value is stated by the administrator (FRD-HRP-026): a tracked type needs all six, an
 * untracked type none. An hours-counted type (short leave, FRD-HRP-080) is untracked and needs all
 * four short-leave values; a days-counted type has none of them. What a type is counted in cannot
 * change afterwards. The API checks the combination and names each missing field.
 */
export const CreateLeaveTypeRequestSchema = z.strictObject({
    code: LeaveTypeCodeSchema,
    name: LeaveTypeNameSchema,
    balanceTracked: z.boolean(),
    annualEntitlementDays: DaysSchema.optional(),
    entitlementPeriod: z.enum(ENTITLEMENT_PERIODS).optional(),
    accrualBasis: z.enum(ACCRUAL_BASES).optional(),
    probationMonths: ProbationMonthsSchema.optional(),
    carryForwardMaxDays: DaysSchema.optional(),
    allowNegativeBalance: z.boolean().optional(),
    countedIn: z.enum(LEAVE_UNITS).optional(),
    monthlyLimitHours: HoursSchema.optional(),
    maxHoursPerRequest: HoursSchema.optional(),
    hoursPerDay: HoursSchema.optional(),
    conversionLeaveTypeId: IdSchema.optional(),
    supportingRequiredAboveDays: DaysSchema.optional(),
});
/** A partial change; a rule value is cleared with `null` (when the type stops tracking). */
export const UpdateLeaveTypeRequestSchema = z.strictObject({
    name: LeaveTypeNameSchema.optional(),
    balanceTracked: z.boolean().optional(),
    annualEntitlementDays: DaysSchema.nullable().optional(),
    entitlementPeriod: z.enum(ENTITLEMENT_PERIODS).nullable().optional(),
    accrualBasis: z.enum(ACCRUAL_BASES).nullable().optional(),
    probationMonths: ProbationMonthsSchema.nullable().optional(),
    carryForwardMaxDays: DaysSchema.nullable().optional(),
    allowNegativeBalance: z.boolean().nullable().optional(),
    monthlyLimitHours: HoursSchema.optional(),
    maxHoursPerRequest: HoursSchema.optional(),
    hoursPerDay: HoursSchema.optional(),
    conversionLeaveTypeId: IdSchema.optional(),
    supportingRequiredAboveDays: DaysSchema.nullable().optional(),
    status: MasterStatusSchema.optional(),
});
// ------------------------------------------------------------------ Leave Balance
export const LeaveBalanceSchema = z.object({
    leaveTypeId: IdSchema,
    leaveTypeCode: z.string(),
    leaveTypeName: z.string(),
    allowNegativeBalance: z.boolean(),
    /** The entitlement period the balance is for; null before the joining date. */
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    entitlement: z.string(),
    carriedForward: z.string(),
    taken: z.string(),
    pending: z.string(),
    remaining: z.string(),
    /** remaining − pending. */
    available: z.string(),
});
/** One short-leave type's hours in the calendar month of `asOf` (FRD-HRP-081). */
export const ShortLeaveAllowanceSchema = z.object({
    leaveTypeId: IdSchema,
    leaveTypeCode: z.string(),
    leaveTypeName: z.string(),
    monthStart: z.string(),
    monthEnd: z.string(),
    limitHours: z.string(),
    maxHoursPerRequest: z.string(),
    /** Approved hours in the month. */
    taken: z.string(),
    /** Hours in approval. */
    pending: z.string(),
    /** limit − taken − pending: what a new request may still use. */
    available: z.string(),
});
export const EmployeeLeaveBalancesSchema = z.object({
    employeeId: IdSchema,
    employeeCode: z.string(),
    employeeName: z.string(),
    asOf: z.string(),
    balances: z.array(LeaveBalanceSchema),
    shortLeave: z.array(ShortLeaveAllowanceSchema).default([]),
});
export const LeaveBalanceQuerySchema = z.strictObject({
    asOf: BusinessDateSchema.optional(),
});
// ------------------------------------------------------------------ Requests (shared)
const RequestEmployeeShape = {
    employeeId: IdSchema,
    employeeCode: z.string(),
    employeeName: z.string(),
    departmentId: IdSchema,
    costCenterId: IdSchema,
    locationId: IdSchema,
    status: RequestStatusSchema,
    /** The user who submitted it: the employee, or HR on the employee's behalf. */
    requestedBy: IdSchema,
    submittedAt: z.string(),
    decidedAt: z.string().nullable(),
    version: z.number().int(),
};
// ------------------------------------------------------------------ Leave Request
export const LeaveRequestSchema = z.object({
    id: IdSchema,
    code: z.string(),
    ...RequestEmployeeShape,
    leaveTypeId: IdSchema,
    leaveTypeCode: z.string(),
    leaveTypeName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    /** `0.0` for short leave, which counts hours and no days. */
    durationDays: z.string(),
    /** The hours asked for, when the request was made in hours (FRD-HRP-081). */
    durationHours: z.string().nullable().default(null),
    /** The short-leave type a request above its hours per request was converted from (HRP-082). */
    convertedFromLeaveTypeId: IdSchema.nullable().default(null),
    convertedFromLeaveTypeName: z.string().nullable().default(null),
    reason: z.string().nullable(),
    /** The colleague covering the work (CLR-46, FRD-HRP-093). */
    delegateEmployeeId: IdSchema.nullable().default(null),
    delegateName: z.string().nullable().default(null),
    /** The leave type's threshold for a reason and a supporting document (FRD-HRP-094). */
    supportingRequiredAboveDays: z.string().nullable().default(null),
});
const LeaveRequestFields = {
    leaveTypeId: IdSchema,
    startDate: BusinessDateSchema,
    endDate: BusinessDateSchema,
    /**
     * For a days-counted type the API computes the duration: the working days of the dates, without
     * the weekend and public holidays (CLR-46, FRD-HRP-090). `durationDays` may be omitted, or sent
     * as that number, or as `0.5` for half of a single working day. An hours-counted type takes
     * `durationHours` and one date (FRD-HRP-081). The API refuses the wrong one for the type.
     */
    durationDays: DaysSchema.optional(),
    durationHours: HoursSchema.optional(),
    reason: z.string().trim().min(1).max(2000).optional(),
    /** Required above `hr.leave_delegate_required_above_days` working days (FRD-HRP-093). */
    delegateEmployeeId: IdSchema.optional(),
};
/** Submits a leave request. Without `employeeId` it is the caller's own (self-service). */
export const SubmitLeaveRequestSchema = z.strictObject({
    employeeId: IdSchema.optional(),
    ...LeaveRequestFields,
});
/** Edits a returned or rejected request and resubmits it (`If-Match`). */
export const ResubmitLeaveRequestSchema = z.strictObject(LeaveRequestFields);
// ------------------------------------------------------------------ Leave Cancellation
/**
 * A request to cancel approved leave (approved R1 decision, 28 Sep 2026; FRD-HRP-030): its own
 * record and approval route; approved, the leave is cancelled and the entitlement restored.
 */
export const LeaveCancellationSchema = z.object({
    id: IdSchema,
    code: z.string(),
    ...RequestEmployeeShape,
    leaveRequestId: IdSchema,
    leaveCode: z.string(),
    leaveStatus: RequestStatusSchema,
    leaveTypeName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    durationDays: z.string(),
    durationHours: z.string().nullable().default(null),
    reason: z.string(),
});
/** Asks to cancel an approved leave request, or resubmits a returned cancellation (`If-Match`). */
export const LeaveCancellationRequestSchema = z.strictObject({
    reason: z.string().trim().min(1).max(2000),
});
// ------------------------------------------------------------------ Employee Request
export const EmployeeRequestTypeSchema = z.object({
    valueCode: z.string(),
    label: z.string(),
    active: z.boolean(),
});
export const EmployeeRequestRecordSchema = z.object({
    id: IdSchema,
    code: z.string(),
    ...RequestEmployeeShape,
    requestTypeCode: z.string(),
    requestTypeLabel: z.string(),
    subject: z.string(),
    description: z.string().nullable(),
});
const EmployeeRequestFields = {
    requestTypeCode: z.string().min(1).max(100),
    subject: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(4000).optional(),
};
/** Submits an employee request. Without `employeeId` it is the caller's own (self-service). */
export const SubmitEmployeeRequestSchema = z.strictObject({
    employeeId: IdSchema.optional(),
    ...EmployeeRequestFields,
});
export const ResubmitEmployeeRequestSchema = z.strictObject(EmployeeRequestFields);
// ------------------------------------------------------------------ Working days (CLR-46)
/** An announced public holiday, not counted in leave (FRD-HRP-091). */
export const PublicHolidaySchema = z.object({
    id: IdSchema,
    holidayDate: z.string(),
    name: z.string(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
export const CreatePublicHolidayRequestSchema = z.strictObject({
    holidayDate: BusinessDateSchema,
    name: z.string().trim().min(1).max(100),
});
/** A moved holiday changes its date; a withdrawn one is made inactive (`If-Match`). */
export const UpdatePublicHolidayRequestSchema = z.strictObject({
    holidayDate: BusinessDateSchema.optional(),
    name: z.string().trim().min(1).max(100).optional(),
    status: MasterStatusSchema.optional(),
});
export const PublicHolidaysQuerySchema = z.strictObject({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const WorkingDaysQuerySchema = z.strictObject({
    startDate: BusinessDateSchema,
    endDate: BusinessDateSchema,
});
/** The duration a leave request in days will have (FRD-HRP-090), for the request form. */
export const WorkingDaysSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
    workingDays: z.number().int(),
    weekendDays: z.number().int(),
    holidays: z.array(z.object({ date: z.string(), name: z.string() })),
    /** A delegate is required above this many working days (FRD-HRP-093). */
    delegateRequiredAboveDays: z.number(),
});
/** A colleague who can be named as delegate: name and Employee ID only (FRD-HRP-093). */
export const LeaveDelegateSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
});
//# sourceMappingURL=hr-leave.js.map